/** @format */

import React, { useState, useEffect, useRef, useContext } from 'react'
import {
  _$,
  Dialog,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Message,
  Tooltip,
  Icon
} from 'kpc-react'
import { CountContext } from 'src/pages/MonitorCloud/MonitorCloudList'
import SelectInstance from 'src/components/SelectInstance'
import {
  CustomInstanceDialogProps,
  CustomInstanceFormValue,
  MetricsItem,
  TagItem,
  handleMetricsList
} from '../constants'
import { request } from 'src/utils'
import styled from 'styled-components'
import API from 'src/api/ApiCloud'
import ApiCommon from 'src/api/ApiCommon'
import _ from 'lodash'

const CustomInstanceDialog: React.FC<CustomInstanceDialogProps> = ({
  visible,
  handleChange,
  defaultFormValue,
  updateTable
}) => {
  const { productType } = useContext(CountContext)
  const [formValue, setFormValue] = useState<CustomInstanceFormValue>({ ...defaultFormValue })
  const [metricsList, setMetricsList] = useState<MetricsItem[]>([])
  const [provinceList, setProvinceList] = useState<TagItem[]>([])
  const [ispList, setIspList] = useState<TagItem[]>([])
  const formRef = useRef(null)

  useEffect(() => {
    if (visible) {
      if (defaultFormValue?.instanceName) {
        formValueChange({ ...defaultFormValue })
      }
      !metricsList?.length && getMetricsList()
    } else {
      formValueChange({ ...defaultFormValue })
    }
  }, [visible, defaultFormValue])

  // 获取监控List
  const getMetricsList = async () => {
    const { data } = await request(ApiCommon.getProductMetricList, {
      data: { ProductType: productType, pageIndex: 1 }
    })

    if (data?.metricList?.length) {
      setMetricsList(data?.metricList)
    }
  }

  // 修改formValue
  const formValueChange = value => {
    setFormValue({
      ...formValue,
      ...value
    })
  }

  const onOk = async dialog => {
    const valid = await formRef.current.validate()
    if (valid) {
      let url = API.createAggregateInstance
      let method = 'POST'
      const { instanceId, instanceName, associatedResources, aggregateTags, region, metric } =
        _.cloneDeep(formValue)
      const params = {
        aggregateType: metric?.aggregateType,
        instanceName,
        instanceId,
        instanceIdList: associatedResources.map(({ instanceId }) => instanceId),
        key_: metric.metricKey,
        name: metric.metricName,
        productType,
        regionKey: region,
        tags: _.cloneDeep(aggregateTags),
        delay: metric.delay + ''
      }
      if (defaultFormValue.instanceId) {
        // 修改
        url = API.modifyAggregateInstance
        method = 'PUT'
      }
      const result = await request(url, {
        method,
        data: {
          ...params
        }
      })
      if (result.code === 'Success') {
        dialog.close()
        updateTable && updateTable()
      }
    }
  }

  // 选择实例、监控项后请求聚合维度
  useEffect(() => {
    if (
      formValue.associatedResources?.length &&
      formValue.metric?.metricKey &&
      formValue.metric?.metricName
    ) {
      getTags()
    }
  }, [formValue.associatedResources, formValue.metric?.metricKey])

  // 获取聚合维度List（省份、运营商）
  const getTags = async () => {
    const InstanceIds = formValue.associatedResources.map(item => item.instanceId)
    const { data } = await request(API.getInstanceMetricTags, {
      method: 'post',
      data: {
        ProductType: parseInt(productType),
        InstanceIds,
        MetricKey: formValue.metric.metricKey,
        MetricName: formValue.metric.metricName
      }
    })
    if (data?.data?.length) {
      data.data.forEach(({ field, values }) => {
        if (field === 'province') {
          setProvinceList(handleMetricsList(values))
        }
        if (field === 'isp') {
          setIspList(handleMetricsList(values))
        }
      })
    } else {
      setProvinceList([])
      setIspList([])
    }
  }

  return (
    <Dialog
      value={visible}
      width={600}
      on$change-value={handleChange}
      title={_$('自定义聚合实例')}
      mode="destroy"
      ok={onOk}
    >
      <Form ref={formRef} labelWidth="120" starOnRequired>
        <FormItem
          label={_$('聚合实例名称')}
          value={formValue.instanceName}
          rules={{ required: true, userName: true }}
        >
          <Input
            value={formValue.instanceName}
            on$change-value={(_c, value) => formValueChange({ instanceName: value })}
          />
        </FormItem>
        <FormItem
          label={_$('选择实例')}
          value={formValue.associatedResources}
          rules={{ required: true }}
        >
          <SelectInstance
            productType={productType}
            allInstance={false}
            instanceGroup={false}
            projectDimens={false}
            defaultRadioValue={3}
            defaultInstance={formValue.associatedResources}
            onChange={(redio, value) => {
              if (redio === 3) {
                formValueChange({ associatedResources: value })
              }
            }}
          />
        </FormItem>
        <FormItem
          label={_$('监控项')}
          value={formValue.metric?.metricKey}
          rules={{ required: true }}
        >
          <Select
            value={formValue.metric?.metricKey}
            onChange={value => {
              const find = metricsList?.find(({ metricKey }) => metricKey === value)
              const update = {
                ...formValue.metric,
                metricKey: value,
                metricName: find.metricName,
                delay: find.delay
              }
              formValueChange({
                metric: { ...update },
                aggregateTags: {
                  province: [],
                  isp: []
                }
              })
              if (!formValue.associatedResources?.length) {
                formRef.current.validate()
                Message.warning(_$('请选择实例'))
              }
            }}
          >
            {metricsList?.length &&
              metricsList.map(item => (
                <Option key={item.metricKey} value={item.metricKey}>
                  {item.metricName}
                </Option>
              ))}
          </Select>
        </FormItem>
        <FormItem
          b-label={
            <React.Fragment>
              {_$('聚合方式')}
              <Tooltip
                content={_$(
                  '聚合方式支持：sum，支持查询5min之前的聚合数据，多个监控项数据分别聚合'
                )}
              >
                <Icon
                  style={{ fontSize: '14px', marginLeft: '4px' }}
                  className="ion-help-circled"
                />
              </Tooltip>
            </React.Fragment>
          }
          value={formValue.metric?.aggregateType}
          rules={{ required: true }}
        >
          <Select
            value={formValue.metric?.aggregateType}
            onChange={value => {
              formValueChange({
                metric: {
                  ...formValue.metric,
                  aggregateType: value
                }
              })
            }}
          >
            <Option value="sum">{_$('求和')}</Option>
          </Select>
        </FormItem>
        {formValue.metric?.metricKey && formValue.associatedResources?.length ? (
          <>
            {provinceList?.length ? (
              <FormItem
                label={_$('省份')}
                value={formValue.aggregateTags.province}
                rules={{ required: true }}
              >
                <Select
                  value={formValue.aggregateTags.province}
                  multiple={true}
                  onChange={value => {
                    formValueChange({
                      aggregateTags: {
                        ...formValue.aggregateTags,
                        province: value
                      }
                    })
                  }}
                >
                  {provinceList.map(item => (
                    <Option key={item.key} value={item.key}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            ) : null}
            {ispList?.length ? (
              <FormItem
                label={_$('运营商')}
                value={formValue.aggregateTags.isp}
                rules={{ required: true }}
              >
                <Select
                  value={formValue.aggregateTags.isp}
                  multiple={true}
                  onChange={value => {
                    formValueChange({
                      aggregateTags: {
                        ...formValue.aggregateTags,
                        isp: value
                      }
                    })
                  }}
                >
                  {ispList.map(item => (
                    <Option key={item.key} value={item.key}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            ) : null}
          </>
        ) : (
          ''
        )}
      </Form>
    </Dialog>
  )
}

export default styled(CustomInstanceDialog)``
